import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ArrowLeft, ExternalLink, Loader2, ChevronDown } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { usePayoutStatus } from "@/hooks/usePayoutStatus";
import { useSyncPayoutStatus } from "@/hooks/useSyncPayoutStatus";
import { toast } from "@/hooks/use-toast";
import { useState } from "react";

const PAYEE_MESSAGE = "Hey — to get paid through Stravo, open Stravo → Profile → Payouts → Connect Stripe.";

export default function PayoutsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnOrRefresh = searchParams.get("return") === "1" || searchParams.get("refresh") === "1";
  const { user, profile, loading: authLoading } = useAuth();
  const { state, row, isLoading, refetch } = usePayoutStatus();
  const syncStatus = useSyncPayoutStatus();
  const [faqOpen, setFaqOpen] = useState(false);
  const [faqWhyOpen, setFaqWhyOpen] = useState(false);
  /** When true, we landed with ?return=1 and are syncing status so we show "Checking your setup…" until done. */
  const [syncingAfterReturn, setSyncingAfterReturn] = useState(false);
  /** When true, user clicked "Switch payout account" and we show the connect options even when connected. */
  const [showSwitchOptions, setShowSwitchOptions] = useState(false);
  const hasRunReturnSync = useRef(false);
  const hasHandledOAuthCode = useRef(false);

  const isPayee = profile?.role === "cleaner" || profile?.role === "company_admin";

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (!authLoading && user && profile && !isPayee) navigate("/account", { replace: true });
  }, [authLoading, user, profile, isPayee, navigate]);

  // Stripe OAuth return: exchange code and redirect to ?return=1 so status refreshes
  useEffect(() => {
    const code = searchParams.get("code");
    const errorParam = searchParams.get("error");
    if (!user || hasHandledOAuthCode.current) return;
    if (errorParam) {
      hasHandledOAuthCode.current = true;
      if (errorParam === "access_denied") {
        toast({ title: "Connection cancelled", description: "You can connect your Stripe account anytime.", variant: "default" });
      } else {
        toast({ title: "Connection failed", description: errorParam, variant: "destructive" });
      }
      navigate("/account/payouts", { replace: true });
      return;
    }
    if (!code) return;
    hasHandledOAuthCode.current = true;
    const stateParam = searchParams.get("state") ?? "";
    (async () => {
      try {
        const { supabase } = await import("@/integrations/supabase/client");
        const { data, error } = await supabase.functions.invoke("stripe-connect-oauth-callback", {
          body: { code, state: stateParam },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        toast({ title: "Stripe account connected" });
        navigate("/account/payouts?return=1", { replace: true });
      } catch (e) {
        toast({
          title: "Could not connect account",
          description: (e as Error).message,
          variant: "destructive",
        });
        navigate("/account/payouts", { replace: true });
      }
    })();
  }, [user?.id, searchParams, navigate]);

  useEffect(() => {
    if (!returnOrRefresh || !user || hasRunReturnSync.current) return;
    hasRunReturnSync.current = true;
    setSyncingAfterReturn(true);
    syncStatus.mutate(undefined, {
      onSettled: () => {
        refetch();
        setSyncingAfterReturn(false);
      },
      onError: () => {
        toast({ title: "Could not refresh status", variant: "destructive" });
        setSyncingAfterReturn(false);
      },
    });
  }, [returnOrRefresh, user?.id]);

  const handleConnectStripe = async (resetConnectAccount = false, resetOnly = false) => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const body = resetConnectAccount
        ? { reset_connect_account: true, ...(resetOnly && { reset_only: true }) }
        : {};
      const { data, error } = await supabase.functions.invoke("create-connect-onboarding-link", {
        body,
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if ((data as { reset?: boolean })?.reset === true) {
        await refetch();
        toast({ title: "Setup cleared", description: "You can now connect your existing Stripe account or start fresh." });
        return;
      }
      const url = (data as { url?: string })?.url;
      if (url) window.location.href = url;
      else throw new Error("No redirect URL");
    } catch (e) {
      toast({
        title: resetConnectAccount ? "Could not reset connect account" : "Could not start Stripe setup",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleConnectExistingStripe = async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("stripe-connect-oauth-link", {});
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const url = (data as { url?: string })?.url;
      if (url) window.location.href = url;
      else throw new Error("No redirect URL");
    } catch (e) {
      toast({
        title: "Could not start connection",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleManageInStripe = async () => {
    if (row?.account_type === "standard") {
      window.open("https://dashboard.stripe.com", "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data, error } = await supabase.functions.invoke("create-connect-login-link", {});
      if (error) throw error;
      if (data?.error) {
        if ((data as { error?: string }).error === "LOGIN_LINK_NOT_AVAILABLE") {
          window.open("https://dashboard.stripe.com", "_blank", "noopener,noreferrer");
          return;
        }
        throw new Error((data as { error?: string }).error);
      }
      const url = (data as { url?: string })?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else throw new Error("No redirect URL");
    } catch (e) {
      toast({
        title: "Could not open Stripe",
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  if (authLoading || !user) return null;
  if (profile && !isPayee) return null;

  const showCheckingSetup = returnOrRefresh && (syncingAfterReturn || syncStatus.isPending);

  return (
    <AppLayout>
      <Navbar />
      <div className="w-full max-w-[560px] mx-auto px-4 sm:px-6 py-6">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/account")}
          className="mb-4 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <div className="space-y-2 mb-6">
          <h1 className="text-xl font-semibold">Choose how you want to get paid</h1>
          <p className="text-sm text-muted-foreground">
            Set up payouts for when owners pay invoices in Stravo, or get paid outside the app.
          </p>
        </div>

        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <CardTitle className="text-base">Get paid in Stravo (Stripe)</CardTitle>
              {showCheckingSetup || isLoading ? (
                <Badge variant="secondary" className="gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {showCheckingSetup ? "Checking your setup…" : "Loading…"}
                </Badge>
              ) : state === "not_started" ? (
                <Badge variant="secondary">Not connected</Badge>
              ) : state === "pending" ? (
                <Badge variant="secondary">Incomplete</Badge>
              ) : (
                <Badge variant="default" className="bg-green-600">Connected</Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {(showCheckingSetup || isLoading) && (
              <p className="text-sm text-muted-foreground">
                {showCheckingSetup ? "Checking your setup…" : "Checking status…"}
              </p>
            )}

            {!showCheckingSetup && !isLoading && (state === "not_started" || showSwitchOptions) && (
              <>
                <p className="text-sm text-muted-foreground">
                  When owners pay an invoice in Stravo, the money is sent to your bank. Get paid with Stravo (recommended) or connect an existing Stripe account.
                </p>
                <p className="text-xs text-muted-foreground">
                  Stripe asks for info when we send you money—required by law; same verification used by marketplaces and gig platforms.
                </p>
                <div className="flex flex-col gap-2">
                  <Button onClick={handleConnectStripe} className="w-full sm:w-auto">
                    Get paid with Stravo (recommended)
                  </Button>
                  <button
                    type="button"
                    onClick={handleConnectExistingStripe}
                    className="text-sm text-primary hover:underline text-left"
                  >
                    Already have Stripe? Connect it
                  </button>
                </div>
                {showSwitchOptions && (
                  <p className="text-xs text-muted-foreground pt-2">
                    <button
                      type="button"
                      className="underline hover:no-underline"
                      onClick={() => setShowSwitchOptions(false)}
                    >
                      Keep current account
                    </button>
                  </p>
                )}
              </>
            )}

            {!showCheckingSetup && !isLoading && state === "pending" && !showSwitchOptions && (
              <>
                <p className="text-sm text-muted-foreground">
                  Finish setting up your Stripe account to enable payouts. If you left Stripe and came back, use the button below to return and pick up where you left off.
                </p>
                <div className="flex flex-col sm:flex-row flex-wrap gap-2">
                  <Button onClick={() => handleConnectStripe(false)} className="w-full sm:w-auto">
                    Continue in Stripe
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleManageInStripe}
                    className="w-full sm:w-auto"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Manage in Stripe
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  Want to log in with your existing Stripe account instead?{" "}
                  <button
                    type="button"
                    className="underline hover:no-underline font-medium"
                    onClick={() => handleConnectStripe(true, true)}
                  >
                    Use my existing Stripe account
                  </button>
                  <span className="text-muted-foreground/90"> — clears this setup and lets you connect via Stripe login.</span>
                </p>
                <p className="text-xs text-muted-foreground pt-1">
                  Or{" "}
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() => handleConnectStripe(true, false)}
                  >
                    start over from the beginning
                  </button>
                  <span className="text-muted-foreground/90"> (new Express setup, same flow as before).</span>
                </p>
              </>
            )}

            {!showCheckingSetup && !isLoading && state === "complete" && !showSwitchOptions && (
              <>
                <p className="text-sm text-muted-foreground">
                  You’re set up to receive payouts when owners pay invoices in Stravo.
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    onClick={handleManageInStripe}
                    className="w-full sm:w-auto"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    {row?.account_type === "standard" ? "Open Stripe Dashboard" : "Manage in Stripe"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate("/account/payments")}
                    className="w-full sm:w-auto"
                  >
                    View payments
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground pt-2">
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() => setShowSwitchOptions(true)}
                  >
                    Switch payout account
                  </button>
                  {" · "}
                  <button
                    type="button"
                    className="underline hover:no-underline"
                    onClick={() => handleConnectStripe(true)}
                  >
                    Reconnect
                  </button>
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="mb-6 border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Get paid outside Stravo</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              If you already charge the property’s card with your own Stripe, or get paid by check, Zelle, etc., you don’t need to connect anything here. Invoices in Stravo are for record-keeping; the owner will mark them paid when they’ve paid you.
            </p>
            <p className="text-xs text-muted-foreground mt-2">
              No setup required—you’re all set.
            </p>
          </CardContent>
        </Card>

        <div className="space-y-1">
          <Collapsible open={faqOpen} onOpenChange={setFaqOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                When do I get paid?
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${faqOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="text-sm text-muted-foreground pl-1 pt-1">
                After an owner pays an invoice. When they use &quot;Pay invoice&quot; in Stravo, the amount is charged to their card and sent to your connected Stripe account. If an owner pays before you’ve connected Stripe, the funds are held and sent to you once you complete setup.
              </p>
            </CollapsibleContent>
          </Collapsible>
          <Collapsible open={faqWhyOpen} onOpenChange={setFaqWhyOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                Why does Stripe ask for my info?
                <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${faqWhyOpen ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <p className="text-sm text-muted-foreground pl-1 pt-1">
                When we send you money, we’re required to verify who’s receiving it. Stripe handles that in one secure flow (identity + bank). It’s a one-time setup; you won’t need to re-enter it unless you change accounts.
              </p>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </div>
    </AppLayout>
  );
}

export { PAYEE_MESSAGE };
